import axios, { AxiosError } from "axios";
import React, { MutableRefObject, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { resetPassword } from "src/redux/api/_admin.api";

type ResetData = {
  email: string;
  newPassword: string;
  digit1: string;
  digit2: string;
  digit3: string;
  digit4: string;
  digit5: string;
  digit6: string;
};

type DigitKey = "digit1" | "digit2" | "digit3" | "digit4" | "digit5" | "digit6";

const ConfirmationCodeInput = () => {

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const form = useForm<ResetData>({
    defaultValues: {
      email: "",
      newPassword: "",
      digit1: "",
      digit2: "",
      digit3: "",
      digit4: "",
      digit5: "",
      digit6: "",
    },
  });

  // Create refs for each input field
  const inputRefs: MutableRefObject<HTMLInputElement | null>[] = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  const getDigitName = (digit: number): DigitKey => {
    return `digit${digit}` as DigitKey;
  };

  // Auto-focus between inputs
  const handleChange =
    (index: number) =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      let value = e.target.value;

      // Only allow one digit
      if (value.length > 1) {
        value = value.slice(0, 1);
      }

      const digitKey = getDigitName(index + 1);
      form.setValue(digitKey, value, { shouldValidate: true });

      // If a digit was entered and there's a next input, move focus
      if (value.length === 1 && index < 5) {
        inputRefs[index + 1].current?.focus();
      }
    };

  const handleKeyDown =
    (index: number) =>
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      const digitKey = getDigitName(index + 1);
      const currentValue = form.getValues(digitKey); // Get current value from form

      if (e.key === "Backspace") {
        e.preventDefault(); // Prevent default backspace behavior

        // If the current field has a value, clear it first
        if (currentValue) {
          form.setValue(digitKey, ""); // Clear the input field
        }
        // If already empty, move focus to the previous field
        else if (index > 0) {
          inputRefs[index - 1].current?.focus();
        }
      }
    };

  const handlePaste = (e: React.ClipboardEvent): void => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");

    // If the pasted content has at least 6 digits
    if (/^\d{6,}/.test(pastedData)) {

      for (let i = 0; i < 6; i++) {
        const digitKey = getDigitName(i + 1);
        form.setValue(digitKey, pastedData[i], { shouldValidate: true });

        const inputElement = inputRefs[i].current;
        if (inputElement !== null) {
          inputElement.value = pastedData[i];
        }
      }

      const lastInput = inputRefs[5].current;
      if (lastInput !== null) {
        lastInput.focus();
      }
    }
  };

  const onSubmit = async (data: ResetData) => {
    setLoading(true);

    const confirmationCode = Object.keys(data)
      .filter((key) => key.startsWith("digit"))
      .sort()
      .map((key) => data[key as keyof ResetData])
      .join("");
    if(confirmationCode && data.email && data.newPassword) {
      try {
      const response = await resetPassword(data.email, confirmationCode, data.newPassword);
      
      if(response.message.toLowerCase() === "success") {
        toast.success("Password Changed Successfully!");
        navigate("/");
      }
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError;
          console.error(error);

          if (axiosError.response) {
            toast.error(error.response?.data.message);
          }
        } else {
          toast.error("Failed to Reset Password!");
          console.error(error);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4">
          <div className="flex gap-4 justify-center" onPaste={handlePaste}>
            {[1, 2, 3, 4, 5, 6].map((digit, index) => {
              const digitName = getDigitName(digit);
              return (
                <input
                  key={digit}
                  type="text"
                  className={`input p-3 rounded-md border-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-400 ${
                    form.formState.errors[digitName]
                      ? "border-red-500"
                      : "border-blue-300"
                  } size-12 text-center text-xl`}
                  maxLength={1}
                  inputMode="numeric"
                  pattern="[0-9]"
                  autoComplete="one-time-code"
                  {...form.register(digitName, {
                    required: "Required",
                    pattern: /^[0-9]$/,
                  })}
                  ref={(e) => {
                    form.register(digitName).ref(e);
                    inputRefs[index].current = e;
                  }}
                  onChange={handleChange(index)}
                  onKeyDown={handleKeyDown(index)}
                />
              );
            })}
          </div>
          <div>
            <div className="flex flex-col gap-3">
              <input
                id="email"
                type="email"
                {...form.register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid Email address",
                  },
                })}
                placeholder="Enter Your Email"
                className="input input-md border-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-400 border-blue-300"
              />
              <input
                id="newPassword"
                type="text"
                {...form.register("newPassword", {
                  required: "Password is required",
                  minLength: {
                    value: 8,
                    message: "Password must be at least 8 characters",
                  },
                })}
                placeholder="Enter Your New Password"
                className="input input-md border-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-400 border-blue-300"
              />
              <div className="px-4 text-sm text-gray-400 flex flex-wrap gap-x-3">
                <p>Minimum 2 Uppercase |</p>
                <p>Minimum 2 Lowercase |</p>
                <p>Minimum 2 Numbers |</p>
                <p>Minimum 2 Symbols</p>
              </div>
            </div>
          </div>
          <div className="my-6 flex justify-center items-center">
            <button
              className="btn btn-md bg-blue-400 text-white hover:text-blue-400"
              type="submit"
            >
              {loading ? (
                <span className="loading loading-dots loading-xl size-12"></span>
              ) : (
                "Reset Password"
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ConfirmationCodeInput;
